import { ReactNode } from 'react';
import { LEGAL } from '../config/legal';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-white/10 dark:bg-gray-900/20 shadow-lg border border-white/20 p-6">
      <h2 className="text-xl font-semibold mb-3 text-white">{title}</h2>
      <div className="text-gray-300 space-y-3">{children}</div>
    </section>
  );
}

export default function Confidentialite() {
  const { seller, retention } = LEGAL;

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-800/80 backdrop-blur-xl flex flex-col items-center p-8 pt-32">
      <div className="max-w-3xl w-full">
        <h1 className="text-4xl font-extrabold mb-8 text-center text-primary-300">
          Politique de confidentialité
        </h1>

        <div className="space-y-6">
          <Section title="1. Responsable du traitement">
            <p>
              <strong>{seller.name}</strong>, {seller.status.toLowerCase()}, SIRET {seller.siret},{' '}
              {seller.address}.
              <br />
              Contact : {seller.email}
            </p>
          </Section>

          <Section title="2. Données collectées">
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Compte :</strong> adresse e-mail, nom, mot de passe (conservé sous forme chiffrée, jamais
                en clair).
              </li>
              <li>
                <strong>Licences :</strong> clé de licence, type, dates, référence du paiement.
              </li>
              <li>
                <strong>Activation de l'application :</strong> adresse e-mail du compte, clé de licence,
                empreinte de l'ordinateur (un code calculé par SHA-256 à partir de son identifiant matériel, qui
                ne permet pas de retrouver cet identifiant) et nom de l'ordinateur.
              </li>
              <li>
                <strong>Journal d'activation :</strong> date, résultat, adresse IP et type d'application.
              </li>
              <li>
                <strong>Paiement :</strong> traité par Stripe. Le vendeur reçoit la confirmation et la
                référence du paiement, jamais les données de la carte.
              </li>
            </ul>
            <p>
              Vos fichiers MP3 restent sur votre ordinateur : l'application ne les lit pas pour les envoyer et
              n'en transmet aucune information à nos serveurs.
            </p>
          </Section>

          <Section title="3. Finalités et bases légales">
            <ul className="list-disc list-inside space-y-1">
              <li>
                Gérer votre compte, vendre et livrer les licences : <em>exécution du contrat</em>.
              </li>
              <li>
                Vérifier une licence et limiter le nombre de postes autorisés, sécuriser le service :{' '}
                <em>intérêt légitime du vendeur</em>.
              </li>
              <li>
                Conserver les données de facturation : <em>obligation légale</em>.
              </li>
            </ul>
          </Section>

          <Section title="4. Destinataires et sous-traitants">
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Supabase</strong> : base de données et authentification (région Europe, Paris).
              </li>
              <li>
                <strong>Stripe</strong> : paiement.
              </li>
              <li>
                <strong>Vercel</strong> : hébergement du site.
              </li>
              <li>
                <strong>Cloudflare</strong> : réseau du site et mesure d'audience anonyme.
              </li>
            </ul>
            <p>Vos données ne sont ni vendues ni cédées à d'autres tiers.</p>
          </Section>

          <Section title="5. Transferts hors de l'Union européenne">
            <p>
              Vercel, Stripe et Cloudflare peuvent traiter des données en dehors de l'Union européenne,
              notamment aux États-Unis. Chacun encadre ces transferts dans ses propres engagements de protection
              des données, consultables sur son site.
            </p>
          </Section>

          <Section title="6. Durées de conservation">
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Journaux d'activation :</strong> {retention.activationLogsMonths} mois, puis suppression
                automatique.
              </li>
              <li>
                <strong>Compte, licences et activations :</strong> tant que le compte existe. Ils sont supprimés
                avec le compte. Un compte désactivé est conservé jusqu'à votre demande de suppression.
              </li>
              <li>
                <strong>Données de facturation :</strong> {retention.billingYears} ans (obligation légale). Après
                la suppression de votre compte, elles sont conservées sans votre nom ni votre adresse e-mail
                dans notre base ; la référence du paiement reste rattachée aux données de paiement que Stripe
                conserve de son côté.
              </li>
            </ul>
          </Section>

          <Section title="7. Vos droits">
            <p>
              Vous disposez d'un droit d'accès, de rectification, d'effacement, d'opposition, de limitation et de
              portabilité de vos données. Vous pouvez supprimer votre compte vous-même depuis la page Profil.
              Pour toute autre demande, écrivez à {seller.email} : nous répondons dans un délai d'un mois.
            </p>
            <p>
              Vous pouvez aussi introduire une réclamation auprès de la CNIL (www.cnil.fr).
            </p>
          </Section>

          <Section title="8. Cookies et traceurs">
            <p>
              Ce site n'utilise aucun cookie publicitaire et ne réalise aucun suivi publicitaire. La session de
              connexion à votre compte est conservée dans le stockage de votre navigateur : elle est nécessaire
              au fonctionnement du site. Lors du paiement, Stripe peut déposer des traceurs destinés à prévenir
              la fraude. La fréquentation du site est mesurée de façon anonyme, sans cookie, par Cloudflare Web
              Analytics.
            </p>
          </Section>
        </div>

        <p className="text-gray-400 text-sm text-center mt-8">
          Dernière mise à jour : 7 octobre 2026
        </p>
      </div>
    </main>
  );
}
